import { Alert, AlertDescription, AlertTitle, Button } from "@beweegbaas/ui";
import { Card, CardContent } from "@beweegbaas/ui";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useEffect, useRef, useState } from "react";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { LayoutAuth } from "#/layouts/layout.auth";
import {
  confirmEmail,
  type ConfirmEmailResult,
} from "#/modules/util.confirm-email/confirm-email.functions";
import { z } from "zod/v4";

export const Route = createFileRoute("/$locale/{-$schoolyear}/confirm-email/")({
  validateSearch: z.object({
    email: z.string().optional().catch(undefined),
    token: z.string().optional().catch(undefined),
  }),
  // Missing email/token → nothing to confirm; send the user to login (portal parity).
  beforeLoad: ({ search, params }) => {
    if (!search.email || !search.token) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/login",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
  },
  component: ConfirmEmailPage,
});

function ConfirmEmailPage() {
  const { email, token } = Route.useSearch();
  const confirm = useServerFn(confirmEmail);
  const [result, setResult] = useState<ConfirmEmailResult | null>(null);
  const started = useRef(false);

  // Confirm once on mount. POST (not the loader) so the login session's Set-Cookie is persisted.
  // The ref guard keeps the single-use token from being spent twice under React StrictMode.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        // email/token are guaranteed present by beforeLoad. On a non-recoverable confirm
        // error the server fn throws a redirect; useServerFn converts it to router.navigate
        // and resolves undefined — keep the pending card while it navigates to resend.
        const result = await confirm({ data: { email: email!, token: token! } });
        if (result) setResult(result);
      } catch (error) {
        captureUnexpectedError(error, "confirm-email");
        setResult({ success: false, error: m["common.something_went_wrong"]() });
      }
    })();
  }, [confirm, email, token]);

  // Confirming (or redirecting to resend) — keep the auth chrome without a result card yet.
  if (!result) {
    return (
      <LayoutAuth>
        <title>{m["auth.confirm_email.page_title"]()}</title>
        <Card className="relative w-full">
          <CardContent>
            <p className="text-muted-foreground text-sm">
              {m["auth.confirm_email.title"]({ email: email ?? "" })}
            </p>
          </CardContent>
        </Card>
      </LayoutAuth>
    );
  }

  if (result.success) {
    return (
      <LayoutAuth>
        <Card className="relative w-full">
          <CardContent>
            <title>{m["auth.confirm_email.page_title"]()}</title>
            <div className="flex flex-col gap-4">
              <Alert variant="default" className="border-none">
                <IconMaterialSymbolsCheckCircleRounded className="text-green-600!" />
                <AlertTitle>
                  {m["auth.confirm_email.title"]({ email: result.user?.email ?? "" })}
                </AlertTitle>
                <AlertDescription>{m["auth.confirm_email.success_message"]({})}</AlertDescription>
              </Alert>
              <Button
                variant="success"
                render={
                  <Link
                    from={Route.fullPath}
                    to="/$locale/{-$schoolyear}/login"
                    params={(prev: Record<string, string>) => prev}
                  />
                }
                nativeButton={false}
              >
                {m["auth.confirm_email.got_to_application"]()}
              </Button>
            </div>
          </CardContent>
        </Card>
      </LayoutAuth>
    );
  }

  return (
    <LayoutAuth>
      <Card className="relative w-full">
        <CardContent>
          <title>{m["auth.confirm_email.page_title"]()}</title>
          <div className="flex flex-col gap-4">
            <Alert variant="destructive" className="border-none">
              <IconMaterialSymbolsError className="text-red-500" />
              <AlertTitle>Er ging iets mis</AlertTitle>
              {result.error ? <AlertDescription>{result.error}</AlertDescription> : null}
            </Alert>
            <Button
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/login"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
            >
              Aanmelden
            </Button>
          </div>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
